import type { FormEvent, ReactNode } from 'react';
import { Send } from 'lucide-react';

export interface ChatComposerProps {
  draft: string;
  onDraft: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  busy: boolean;
  placeholder: string;
  sendLabel: string;
  maxLength: number;
  /** Anything above the input row — the internal-note toggle in ticket chat. */
  children?: ReactNode;
  className?: string;
  /** Marks the form for layout tests; the ticket page pins it in the toolbar's slot. */
  slot?: string;
}

/**
 * The one composer both threads use: a rounded input and a round send control, both
 * clearing 48px for a gloved thumb. Positioning is the caller's — the DM thread keeps it
 * in flow at the foot of its column, the ticket page pins it above the tab bar (D53).
 */
export function ChatComposer({
  draft,
  onDraft,
  onSubmit,
  busy,
  placeholder,
  sendLabel,
  maxLength,
  children,
  className = '',
  slot,
}: ChatComposerProps) {
  return (
    <form
      data-slot={slot}
      onSubmit={onSubmit}
      className={`flex flex-col gap-2 border-t border-slate-800 p-3 ${className}`}
    >
      {children}
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(event) => onDraft(event.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          aria-label={placeholder}
          className="min-h-[48px] flex-1 rounded-full border border-slate-700 bg-slate-900 px-4 text-base text-slate-100"
        />
        <button
          type="submit"
          disabled={!draft.trim() || busy}
          aria-label={sendLabel}
          className="flex size-12 shrink-0 items-center justify-center rounded-full bg-brookfields-green text-white ring-1 ring-brookfields-gold/70 disabled:bg-brookfields-green-dark disabled:text-slate-400 disabled:ring-brookfields-gold-dark"
        >
          <Send className="size-5" aria-hidden="true" />
        </button>
      </div>
    </form>
  );
}
